import { ProcessedBidResponse } from 'ts-types';

export type BidResponses = {
  [key: string]: {
    bids: {
      ad: string;
      creativeId: number;
      appnexus?: { buyerMemberId: number };
      cpm: number;
      bidderCode: string;
      auctionId: string;
    }[];
  };
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const pollBidResponses = async () => {
  let pollCounter = 0;
  let responses;

  while (!responses && pollCounter < 10) {
    /* eslint-disable no-await-in-loop */
    await sleep(200);
    pollCounter += 1;
    try {
      responses = (<any>window).pbjs.getBidResponses();
      if (Object.keys(responses).length === 0) responses = undefined;
    } catch (e) {
      // ignore...
    }
  }

  return responses;
};

export const findAuctionIdsFromAd = (ad: string): string[] => {
  const stringsToSearch = ['impId=', 'AUCTION_ID=', 'auction_id='];
  const isNumberRegExp = /^\d*$/gi;

  const auctionIds: string[] = [];

  stringsToSearch.forEach((string) => {
    const targetIndex = ad.search(string);
    if (targetIndex !== -1) {
      const subAd = ad.substring(targetIndex);
      const ampIndex = subAd.indexOf('&');
      const quoteIndex = subAd.indexOf('"');
      const possibleIndexes = [];
      if (ampIndex !== -1) possibleIndexes.push(ampIndex);
      if (quoteIndex !== -1) possibleIndexes.push(quoteIndex);
      const endIndex = Math.min(...possibleIndexes);
      const auctionId = subAd.substring(string.length, endIndex);
      if (
        auctionId &&
        !auctionIds.includes(auctionId) &&
        isNumberRegExp.exec(auctionId)
      )
        auctionIds.push(auctionId);
    }
  });
  return auctionIds;
};

export const processBidResponses = (bidResponses: BidResponses) => {
  return Object.values(bidResponses)
    .map((adUnit) => adUnit.bids)
    .flat()
    .filter((bid) => bid.bidderCode === 'appnexus')
    .map((bid) => {
      const buyerAuctionIds = findAuctionIdsFromAd(bid.ad);
      return {
        ...bid,
        buyerAuctionIds,
      };
    })
    .map(({ auctionId, buyerAuctionIds, cpm, creativeId, appnexus }) => {
      return {
        cpm,
        auctionId,
        buyerAuctionIds,
        creativeId,
        buyerMemberId: appnexus ? appnexus.buyerMemberId : 0,
      };
    });
};

const containsTargetBid = (
  processedBidResponses: ProcessedBidResponse[]
): boolean => {
  return processedBidResponses.some((bid) => bid.buyerMemberId === 668);
};

export const prebidAnalytics = async (): Promise<boolean> => {
  const bidResponses = await pollBidResponses();
  if (!bidResponses) return false;
  const processedBidResponses = processBidResponses(bidResponses);
  const toSequence = containsTargetBid(processedBidResponses);
  return toSequence;
};
